import { getDocument } from "~/liferay/safe-api";
import type { StoresContext } from "~/stores";
import { parseWebContent } from "./web-content";
import type { WebContentValues } from "./web-content";

export function getField<F extends FragmentField>(key: string, fragment: Fragment): F {
  try {
    return fragment.definition.fragmentFields.find(field => field.id == key) as F;
  } catch (error) {
    console.error(`Cannot read field "${key}" from`, fragment);
    return {} as F;
  }
}

export function getFragmentConfig<T>(key: string, fragment: Fragment): T | undefined {
  return fragment.definition.fragmentConfig[key] as T;
}

export function getFragmentConfigOr<T>(key: string, fragment: Fragment, default_value: T): T {
  return getFragmentConfig(key, fragment) || default_value;
}

export function getConfigWebContent (name: string, fragment: Fragment): WebContentValues {

  const result: Record<string, any> = {};
  const config = getFragmentConfig<FragmentConfig_WebContent>(name, fragment);
  if (!config || config.className != 'com.liferay.journal.model.JournalArticle') return {};

  try {
    return parseWebContent(config.content)
  } catch (error) {
    console.error(error);
    return result;
  }
}

export function getHtmlValue(fragment: Fragment): FragmentFieldHtml {

  const field = getField<FragmentFieldHtml>('element-html', fragment);
  return field || {
    id: "element-html",
    value: {
      html: { value_i18n: {} },
      fragmentLink: null,
    }
  }
}

export function getImageValue(fragmentId: string, fragment: Fragment): FragmentFieldImage {
  return getField<FragmentFieldImage>(fragmentId ? fragmentId : 'image-square', fragment);
}

export function getTextValue(fragment: Fragment): FragmentFieldText {
  return getField<FragmentFieldText>('element-text', fragment) || {
    id: "element-text",
    value: {
      text: { value_i18n: {} },
      fragmentLink: null,
    }
  };
}


/** @deprecated Use valueGetters instead */
export function getImage(name_field: string, fragment: Fragment, valueOf: (value: string | Value<string> | Value_i18n, _default?: string) => string) {
  const { value } = getImageValue(name_field, fragment);
  const { fragmentImage } = value;
  return valueOf(fragmentImage.url || "")

}

/** @deprecated Use valueGetters instead */
export function getText(name_field: string, fragment: Fragment, valueOf: (value: string | Value<string> | Value_i18n, _default?: string) => string) {
  const text_field = getField<FragmentFieldText>(
    name_field,
    fragment
  );
  return valueOf(text_field.value.text)
}

/** @deprecated Use valueGetters instead */
export function getLink(name_field: string, fragment: Fragment, valueOf: (value: string | Value<string> | Value_i18n, _default?: string) => string) {
  const link_field = getField<FragmentFieldText>(name_field, fragment);
  return valueOf(link_field.value.fragmentLink.value.href)
}



export type DirectImage = { src: string; alt: string };
export type MappingImage = { className: string; classPK: number };
export type Target = "_blank" | "_self";
export type Link = { text: string; href: string; outbound: boolean; target?: Target | undefined; };

const internal = {
  getImage (name_field: string, fragment: Fragment, stores_context: StoresContext): DirectImage | MappingImage | undefined {
    const { value } = getImageValue(name_field, fragment);
    const { fragmentImage } = value;

    if (!fragmentImage) return;
  
    if ('title' in fragmentImage || 'description' in fragmentImage) {
      const desc_val = fragmentImage.description ? stores_context.valueOf(fragmentImage.description) : undefined;
      const title_val = fragmentImage.title ? stores_context.valueOf(fragmentImage.title) : undefined;
      
      return {
        src: stores_context.valueOfURL(fragmentImage.url),
        alt: desc_val || title_val || ''
      }
    }
    // Mapping to document
    if ('mapping' in fragmentImage.url) {
      return fragmentImage.url.mapping.itemReference;
    }
  },
  getHtml (name_field: string, fragment: Fragment, stores_context: StoresContext) {
    const field = getField<FragmentFieldHtml>(name_field, fragment);
    if (!field) return ''
    return stores_context.valueOf(field.value.html)
  },
  async getAsDirectImage (name_field: string, fragment: Fragment, stores_context: StoresContext): Promise<DirectImage> {

    const image = internal.getImage(name_field, fragment, stores_context);

    if (!image) return Promise.reject(`Missing field "${name_field}"`);
    else if ('src' in image) {
      return image;
    }
    
    // Mapping to document
    const document = await getDocument(image.classPK);
    
    return {
      src: document?.contentUrl || '',
      alt: document?.title || ''
    };
  },
  getLink (name_field: string, fragment: Fragment, stores_context: StoresContext): Link {
    const field = getField<FragmentFieldLink>(name_field, fragment);
    const fragmentLink = field?.value.fragmentLink;

    if (!fragmentLink) return { text: '', href: '', outbound: false };

    const target = fragmentLink?.value?.target;
    const href_value = fragmentLink?.value?.href;

    let href: string | undefined;
    
    if (href_value && 'mapping' in href_value) {
      href = internal.getLayoutRef(href_value.mapping.itemReference, stores_context);
    }
    else if (href_value) {
      href = stores_context.valueOf(href_value);
    }

    return {
      text: stores_context.valueOf(field?.value?.text),
      href: href || '',
      outbound: target == 'Blank',
      target: target == 'Blank' ? '_blank' : target == 'Self' ? '_self' : undefined,
    }
  },

  getText (name_field: string, fragment: Fragment, stores_context: StoresContext) {
    const text_field = getField<FragmentFieldText>(name_field, fragment);
    return stores_context.valueOf(text_field?.value?.text)
  },

  getStyle (fragment: Fragment) {
    return fragment.definition.fragmentStyle ? fragment.definition.fragmentStyle : {};
  },

  getLayoutRef (layout_ref: Value_FragmentLink_Layout, stores_context: StoresContext) {
    if (layout_ref?.className == 'com.liferay.portal.kernel.model.Layout') {
      const field = layout_ref.fields.find(x => x.fieldName == 'friendlyURL');
      return stores_context.reverse_router.get().routePage(field?.fieldValue || '');
    }
    return;
  }   
}

export const valueGetters = (stores_context: StoresContext) => ({
  getImage (name_field: string, fragment: Fragment): DirectImage | MappingImage | undefined {
    return internal.getImage(name_field, fragment, stores_context);
  },

  getStyle (fragment: Fragment) {
    return internal.getStyle(fragment);
  },

  async getAsDirectImage (name_field: string, fragment: Fragment): Promise<DirectImage> {
    return internal.getAsDirectImage(name_field, fragment, stores_context);
  },

  getLink (name_field: string, fragment: Fragment): Link {
    return internal.getLink(name_field, fragment, stores_context);
  },

  getText (name_field: string, fragment: Fragment) {
    return internal.getText(name_field, fragment, stores_context);
  },
  getHtml (name_field: string, fragment: Fragment): string{
    return internal.getHtml(name_field, fragment, stores_context);
  },
  getFragmentConfig,
  getFragmentConfigOr,
  getConfigWebContent (name: string, fragment: Fragment): WebContentValues {
    return getConfigWebContent(name, fragment);
  }
})

/**
 * Scoped value getters tied to passed fragment
 * @param stores_context 
 * @param fragment 
 * @returns 
 */
export const scopedValueGetters = (stores_context: StoresContext, fragment: Fragment) => ({
  getImage (name_field: string): DirectImage | MappingImage | undefined {
    return internal.getImage(name_field, fragment, stores_context);
  },

  getStyle () {
    return internal.getStyle(fragment);
  },

  async getAsDirectImage (name_field: string): Promise<DirectImage> {
    return internal.getAsDirectImage(name_field, fragment, stores_context);
  },

  getLink (name_field: string): Link {
    return internal.getLink(name_field, fragment, stores_context);
  },

  getText (name_field: string) {
    return internal.getText(name_field, fragment, stores_context);
  },
  getHtml (name_field: string): string{
    return internal.getHtml(name_field, fragment, stores_context);
  },

  getFragmentConfig<T>(key: string): T | undefined {
    return getFragmentConfig<T>(key, fragment);
  },

  getFragmentConfigOr<T>(key: string, default_value: T): T {
    return getFragmentConfig(key, fragment) || default_value;
  },

  getConfigWebContent (name: string): WebContentValues {
    return getConfigWebContent(name, fragment);
  }
})
